import axios from 'axios';
import {
  CreateFormTemplatePayload,
  FormTemplate,
  PaginatedFormResponses,
  PendingFormItem,
  FormResponse,
  PesquisaPublicaView,
  PesquisaShareResult,
  SubmitFormResponsePayload,
  UpdateFormTemplatePayload,
} from '../views/formularios/interfaces/forms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function getFormTemplates(params?: { manage?: boolean }): Promise<FormTemplate[]> {
  const response = await axios.get(`${BACKEND_URL}/forms/templates`, {
    params,
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getPendingForms(): Promise<PendingFormItem[]> {
  const response = await axios.get(`${BACKEND_URL}/forms/pending`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getPendingFormsCount(): Promise<number> {
  const response = await axios.get(`${BACKEND_URL}/forms/pending/count`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data.count;
}

export async function getFormTemplate(id: string): Promise<FormTemplate> {
  const response = await axios.get(`${BACKEND_URL}/forms/templates/${id}`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function createFormTemplate(dto: CreateFormTemplatePayload): Promise<FormTemplate> {
  const response = await axios.post(`${BACKEND_URL}/forms/templates`, dto, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function updateFormTemplate(id: string, dto: UpdateFormTemplatePayload): Promise<FormTemplate> {
  const response = await axios.patch(`${BACKEND_URL}/forms/templates/${id}`, dto, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function duplicateFormTemplate(id: string): Promise<FormTemplate> {
  const response = await axios.post(`${BACKEND_URL}/forms/templates/${id}/duplicate`, undefined, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function deleteFormTemplate(id: string): Promise<FormTemplate> {
  const response = await axios.delete(`${BACKEND_URL}/forms/templates/${id}`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function submitFormResponse(templateId: string, dto: SubmitFormResponsePayload): Promise<FormResponse> {
  const response = await axios.post(`${BACKEND_URL}/forms/templates/${templateId}/responses`, dto, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getFormResponses(templateId: string, page = 1, limit = 20): Promise<PaginatedFormResponses> {
  const response = await axios.get(`${BACKEND_URL}/forms/templates/${templateId}/responses`, {
    params: { page, limit },
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getFormResponse(templateId: string, responseId: string): Promise<FormResponse> {
  const response = await axios.get(`${BACKEND_URL}/forms/templates/${templateId}/responses/${responseId}`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

// ------- Pesquisa de satisfação do associado (rotas internas) -------

/** Gera (ou reaproveita) o link da pesquisa do atendimento + a mensagem configurada. */
export async function createPesquisaShare(carId: string): Promise<PesquisaShareResult> {
  const response = await axios.post(`${BACKEND_URL}/forms/pesquisa/cars/${carId}/share`, undefined, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data;
}

export async function getPesquisaMensagem(): Promise<string> {
  const response = await axios.get(`${BACKEND_URL}/forms/pesquisa/mensagem`, {
    headers: authHeader(),
    withCredentials: true,
  });
  return response.data.value;
}

export async function updatePesquisaMensagem(value: string): Promise<string> {
  const response = await axios.put(
    `${BACKEND_URL}/forms/pesquisa/mensagem`,
    { value },
    { headers: authHeader(), withCredentials: true },
  );
  return response.data.value;
}

// ------- Rotas públicas da pesquisa (sem autenticação) -------
//
// Instância própria de axios, SEM os interceptors do app: o interceptor de 401
// tenta refresh e navega para /login, o que na tela do associado — que não tem
// login — desviaria o usuário no meio da pesquisa.
const publicApi = axios.create({ baseURL: BACKEND_URL });

export async function getPesquisaPublicaInfo(
  token: string,
): Promise<{ valid: boolean; respondida: boolean; expiresAt: string }> {
  const response = await publicApi.get(`/public/pesquisa/${token}`);
  return response.data;
}

export async function verificarPesquisaPublica(token: string, phone: string): Promise<PesquisaPublicaView> {
  const response = await publicApi.post(`/public/pesquisa/${token}/verify`, { phone });
  return response.data;
}

export async function responderPesquisaPublica(
  token: string,
  phone: string,
  answers: { fieldId: string; value: string | string[] }[],
): Promise<{ id: string; createdAt: string }> {
  const response = await publicApi.post(`/public/pesquisa/${token}/responder`, { phone, answers });
  return response.data;
}

// ------- Totem da oficina (ADR 0032): identificação por placa + telefone -------

export async function verificarPesquisaTotem(plate: string, phone: string): Promise<PesquisaPublicaView> {
  const response = await publicApi.post('/public/pesquisa-totem/verify', { plate, phone });
  return response.data;
}

export async function responderPesquisaTotem(
  plate: string,
  phone: string,
  answers: { fieldId: string; value: string | string[] }[],
): Promise<{ id: string; createdAt: string }> {
  const response = await publicApi.post('/public/pesquisa-totem/responder', { plate, phone, answers });
  return response.data;
}

export async function exportFormResponsesCsv(templateId: string, title: string): Promise<void> {
  const response = await axios.get(`${BACKEND_URL}/forms/templates/${templateId}/responses/export`, {
    headers: authHeader(),
    withCredentials: true,
    responseType: 'blob',
  });

  const date = new Date().toISOString().split('T')[0];
  const safeTitle = title.replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `respostas-${safeTitle}-${date}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
